import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '@/utils/api';

// Types
export interface NoteFolder {
  id: string;
  userId: string;
  name: string;
  parentId?: string;
  icon?: string;
  color: string;
  folderOrder: number;
  isSmart: boolean;
  smartFilter?: string;
  isSystem?: boolean;
  entityType?: string;
  entityId?: string;
  noteCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface Note {
  id: string;
  userId: string;
  folderId?: string;
  title: string;
  content: string;
  preview?: string;
  isPinned: boolean;
  isLocked: boolean;
  isArchived: boolean;
  isTrashed: boolean;
  trashedAt?: string;
  color?: string;
  tags: string[];
  attachments: string[];
  checklistCount: number;
  checklistCompleted: number;
  wordCount: number;
  linkedEntityType?: string;
  linkedEntityId?: string;
  createdAt: string;
  updatedAt: string;
  folderName?: string;
}

export interface NoteStats {
  total: number;
  active: number;
  pinned: number;
  archived: number;
  trashed: number;
  totalWords: number;
}

interface NotesState {
  folders: NoteFolder[];
  notes: Note[];
  currentNote: Note | null;
  selectedFolderId: string | null;
  stats: NoteStats | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  searchQuery: string;
  viewMode: 'all' | 'folder' | 'archived' | 'trash' | 'task-attachments';
}

const initialState: NotesState = {
  folders: [],
  notes: [],
  currentNote: null,
  selectedFolderId: null,
  stats: null,
  isLoading: false,
  isSaving: false,
  error: null,
  searchQuery: '',
  viewMode: 'all',
};

// Async Thunks

// Folders
export const fetchFolders = createAsyncThunk(
  'notes/fetchFolders',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/notes/folders');
      return response.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch folders');
    }
  }
);

export const createFolder = createAsyncThunk(
  'notes/createFolder',
  async (data: { name: string; parentId?: string; icon?: string; color?: string }, { rejectWithValue }) => {
    try {
      const response = await api.post('/notes/folders', data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to create folder');
    }
  }
);

export const updateFolder = createAsyncThunk(
  'notes/updateFolder',
  async ({ id, data }: { id: string; data: Partial<NoteFolder> }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/notes/folders/${id}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update folder');
    }
  }
);

export const deleteFolder = createAsyncThunk(
  'notes/deleteFolder',
  async (id: string, { rejectWithValue }) => {
    try {
      await api.delete(`/notes/folders/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to delete folder');
    }
  }
);

// Notes
export const fetchNotes = createAsyncThunk(
  'notes/fetchNotes',
  async (params: { folderId?: string; search?: string; tag?: string; trashed?: boolean; archived?: boolean } = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.folderId) queryParams.set('folderId', params.folderId);
      if (params.search) queryParams.set('search', params.search);
      if (params.tag) queryParams.set('tag', params.tag);
      if (params.trashed) queryParams.set('trashed', 'true');
      if (params.archived) queryParams.set('archived', 'true');

      const response = await api.get(`/notes?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch notes');
    }
  }
);

export const fetchNote = createAsyncThunk(
  'notes/fetchNote',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/notes/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch note');
    }
  }
);

export const createNote = createAsyncThunk(
  'notes/createNote',
  async (data: { title?: string; content?: string; folderId?: string | null; isPinned?: boolean; color?: string; tags?: string[] }, { rejectWithValue }) => {
    try {
      const response = await api.post('/notes', data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to create note');
    }
  }
);

export const updateNote = createAsyncThunk(
  'notes/updateNote',
  async ({ id, data }: { id: string; data: Partial<Note> }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/notes/${id}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update note');
    }
  }
);

export const deleteNote = createAsyncThunk(
  'notes/deleteNote',
  async (id: string, { rejectWithValue }) => {
    try {
      await api.delete(`/notes/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to delete note');
    }
  }
);

export const trashNote = createAsyncThunk(
  'notes/trashNote',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await api.post(`/notes/${id}/trash`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to trash note');
    }
  }
);

export const restoreNote = createAsyncThunk(
  'notes/restoreNote',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await api.post(`/notes/${id}/restore`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to restore note');
    }
  }
);

export const togglePinNote = createAsyncThunk(
  'notes/togglePinNote',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await api.post(`/notes/${id}/pin`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to toggle pin');
    }
  }
);

export const emptyTrash = createAsyncThunk(
  'notes/emptyTrash',
  async (_, { rejectWithValue }) => {
    try {
      await api.delete('/notes/trash/empty');
      return true;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to empty trash');
    }
  }
);

export const fetchNoteStats = createAsyncThunk(
  'notes/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/notes/stats/summary');
      return response.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch stats');
    }
  }
);

// Task Notes
export const fetchTaskAttachmentsFolder = createAsyncThunk(
  'notes/fetchTaskAttachmentsFolder',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/notes/folders/task-attachments');
      return response.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch task attachments folder');
    }
  }
);

export const fetchTaskNote = createAsyncThunk(
  'notes/fetchTaskNote',
  async (cardId: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/notes/task/${cardId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch task note');
    }
  }
);

export const saveTaskNote = createAsyncThunk(
  'notes/saveTaskNote',
  async ({ cardId, title, content }: { cardId: string; title: string; content: string }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/notes/task/${cardId}`, { title, content });
      return response.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to save task note');
    }
  }
);

export const deleteTaskNote = createAsyncThunk(
  'notes/deleteTaskNote',
  async (cardId: string, { rejectWithValue }) => {
    try {
      await api.delete(`/notes/task/${cardId}`);
      return cardId;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to delete task note');
    }
  }
);

// Slice
const notesSlice = createSlice({
  name: 'notes',
  initialState,
  reducers: {
    setSelectedFolder: (state, action: PayloadAction<string | null>) => {
      state.selectedFolderId = action.payload;
    },
    setCurrentNote: (state, action: PayloadAction<Note | null>) => {
      state.currentNote = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    setViewMode: (state, action: PayloadAction<'all' | 'folder' | 'archived' | 'trash' | 'task-attachments'>) => {
      state.viewMode = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    updateCurrentNoteLocally: (state, action: PayloadAction<Partial<Note>>) => {
      if (state.currentNote) {
        state.currentNote = { ...state.currentNote, ...action.payload };
      }
    },
  },
  extraReducers: (builder) => {
    // Folders
    builder
      .addCase(fetchFolders.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchFolders.fulfilled, (state, action) => {
        state.isLoading = false;
        state.folders = action.payload;
      })
      .addCase(fetchFolders.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(createFolder.fulfilled, (state, action) => {
        state.folders.push(action.payload);
      })
      .addCase(updateFolder.fulfilled, (state, action) => {
        const index = state.folders.findIndex(f => f.id === action.payload.id);
        if (index !== -1) {
          state.folders[index] = action.payload;
        }
      })
      .addCase(deleteFolder.fulfilled, (state, action) => {
        state.folders = state.folders.filter(f => f.id !== action.payload);
        if (state.selectedFolderId === action.payload) {
          state.selectedFolderId = null;
        }
      });

    // Notes
    builder
      .addCase(fetchNotes.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchNotes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.notes = action.payload;
      })
      .addCase(fetchNotes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchNote.fulfilled, (state, action) => {
        state.currentNote = action.payload;
      })
      .addCase(createNote.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(createNote.fulfilled, (state, action) => {
        state.isSaving = false;
        state.notes.unshift(action.payload);
        state.currentNote = action.payload;
      })
      .addCase(createNote.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload as string;
      })
      .addCase(updateNote.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(updateNote.fulfilled, (state, action) => {
        state.isSaving = false;
        const index = state.notes.findIndex(n => n.id === action.payload.id);
        if (index !== -1) {
          state.notes[index] = action.payload;
        }
        if (state.currentNote?.id === action.payload.id) {
          state.currentNote = action.payload;
        }
      })
      .addCase(updateNote.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload as string;
      })
      .addCase(deleteNote.fulfilled, (state, action) => {
        state.notes = state.notes.filter(n => n.id !== action.payload);
        if (state.currentNote?.id === action.payload) {
          state.currentNote = null;
        }
      })
      .addCase(trashNote.fulfilled, (state, action) => {
        const index = state.notes.findIndex(n => n.id === action.payload.id);
        if (index !== -1) {
          state.notes.splice(index, 1);
        }
        if (state.currentNote?.id === action.payload.id) {
          state.currentNote = null;
        }
      })
      .addCase(restoreNote.fulfilled, (state, action) => {
        const index = state.notes.findIndex(n => n.id === action.payload.id);
        if (index !== -1) {
          state.notes[index] = action.payload;
        } else {
          state.notes.unshift(action.payload);
        }
      })
      .addCase(togglePinNote.fulfilled, (state, action) => {
        const index = state.notes.findIndex(n => n.id === action.payload.id);
        if (index !== -1) {
          state.notes[index] = action.payload;
        }
        if (state.currentNote?.id === action.payload.id) {
          state.currentNote = action.payload;
        }
      })
      .addCase(emptyTrash.fulfilled, (state) => {
        state.notes = state.notes.filter(n => !n.isTrashed);
      })
      .addCase(fetchNoteStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      });
  },
});

export const {
  setSelectedFolder,
  setCurrentNote,
  setSearchQuery,
  setViewMode,
  clearError,
  updateCurrentNoteLocally,
} = notesSlice.actions;

export default notesSlice.reducer;
